<template>
  <div>
    <el-descriptions title="User Info">
      <el-descriptions-item label="Email">
        <span>
          {{ $keycloak.tokenParsed.email }}
        </span>
      </el-descriptions-item>
      <el-descriptions-item label="Email Verified">
        <i v-if="$keycloak.tokenParsed.email_verified" class="el-input__icon el-icon-success">
        </i>
        <span v-else> X </span>
      </el-descriptions-item>
      <el-descriptions-item label="Role">
        {{ role }}
      </el-descriptions-item>
      <el-descriptions-item label="Name">
        {{ $keycloak.tokenParsed.given_name }} {{ $keycloak.tokenParsed.family_name }}
      </el-descriptions-item>
      <el-descriptions-item label="Phone">
        {{ phone || '-' }}
      </el-descriptions-item>
      <el-descriptions-item label="Location">
        {{ displayLocation }}
      </el-descriptions-item>
      <el-descriptions-item label="Profile Image">
        <img
          v-if="profile"
          :src="profile"
          alt="Profile"
          width="40"
          height="40"
          style="display:inline-block;width:40px;height:40px;border-radius:50%;border:0;outline:none;"
        />
        <span v-else>-</span>
      </el-descriptions-item>
    </el-descriptions>
    <el-button style="margin-top: 12px;" @click="profileDialog = true">Update Profile</el-button>
    <el-button style="margin-top: 12px;" @click="signatureDialog = true">Preview Signature</el-button>

    <el-dialog
      :visible.sync="profileDialog"
      title="Update Profile"
      width="640px"
    >
      <el-form>
        <el-form-item label="Profile Image" prop="profile">
          <Upload
            listType="picture"
            @upload-completed="setProfile"
            @remove-upload="clearProfile"
          />
          <el-input v-model="profile" placeholder="Profile image URL"></el-input>
        </el-form-item>
        <el-form-item label="Phone" prop="phone">
          <el-input v-model="phone" :disabled="!isPhoneEditable"></el-input>
        </el-form-item>
        <el-form-item label="Location" prop="department">
          <el-select v-model="department" placeholder="Select location" filterable style="width: 100%;" :disabled="!canEditLocation">
            <el-option
              v-for="option in locationOptions"
              :key="option.value || option.label"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>
        <el-button @click="saveProfileUpdates">Save</el-button>
      </el-form>
    </el-dialog>
    <el-dialog
      :visible.sync="signatureDialog"
      title="Signature Preview"
      width="720px"
    >
      <SignaturePreview
        ref="profileSignaturePreview"
        :profile="profile"
        :name="signatureName"
        :email="signatureEmail"
        :phone="phone"
        :title="title"
        :department="department"
        :address="signatureAddress"
        :logo-url="logoUrl"
      />
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Upload from './Uploader';
import SignaturePreview from './SignaturePreview';
import { AUTH_API_BASE } from '../env';
import { getLocationsForPortal } from '../utils/commonApi';

export default {
  name: 'Profile',
  components: {
    Upload,
    SignaturePreview,
  },
  data() {
    return {
      manager: null,
      profile: null,
      phone: null,
      department: null,
      title: null,
      profileDialog: false,
      signatureDialog: false,
      logoUrl: 'https://email-attachments-public.s3.us-east-2.amazonaws.com/rs.png',
      portalLocations: [],
      manualPhone: null,
    };
  },
  computed: {
    role() {
      if (this.$keycloak.realmAccess.roles.includes('level_1')) return 'LEVEL 1';
      if (this.$keycloak.realmAccess.roles.includes('level_2')) return 'LEVEL 2';
      if (this.$keycloak.realmAccess.roles.includes('level_3')) return 'LEVEL 3';
      if (this.$keycloak.realmAccess.roles.includes('level_4')) return 'LEVEL 4';
      return 'Unknown';
    },
    canEditLocation() {
      const roles = (this.$keycloak && this.$keycloak.realmAccess && this.$keycloak.realmAccess.roles) || [];
      return roles.includes('level_1') || roles.includes('level_2');
    },
    managerId() {
      return this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.sub ? this.$keycloak.tokenParsed.sub : null;
    },
    signatureName() {
      const name = [this.$keycloak.tokenParsed.given_name, this.$keycloak.tokenParsed.family_name].filter(Boolean).join(' ').trim();
      if (name) {
        return name;
      }
      if (this.manager) {
        const managerName = [this.manager.firstName, this.manager.lastName].filter(Boolean).join(' ').trim();
        if (managerName) {
          return managerName;
        }
        if (this.manager.username) {
          return this.manager.username;
        }
        if (this.manager.email) {
          return this.manager.email;
        }
      }
      return 'Signature';
    },
    signatureEmail() {
      return (this.manager && this.manager.email) || this.$keycloak.tokenParsed.email || '';
    },
    signatureAddress() {
      if (!this.department) {
        return '';
      }
      const match = (this.portalLocations || []).find((facility) => this.isLocationMatch(facility, this.department));
      if (!match) {
        return '';
      }
      return this.getLocationAddress(match);
    },
    displayLocation() {
      if (!this.department) {
        return 'None';
      }
      const match = (this.portalLocations || []).find((facility) => this.isLocationMatch(facility, this.department));
      if (match) {
        return this.getLocationLabel(match, this.department);
      }
      return this.department || '-';
    },
    isPhoneEditable() {
      return !this.department;
    },
    locationOptions() {
      const options = [];
      const seen = new Set();

      if (Array.isArray(this.portalLocations) && this.portalLocations.length) {
        this.portalLocations.forEach((facility) => {
          const value = this.getLocationValue(facility);
          if (!value || seen.has(value)) {
            return;
          }
          const label = this.getLocationLabel(facility, value);
          seen.add(value);
          options.push({ label, value });
        });
      } else {
        const rawLocations = (this.manager && this.manager.attributes && this.manager.attributes.locations)
          || (this.$keycloak && this.$keycloak.tokenParsed && this.$keycloak.tokenParsed.locations)
          || [];
        const locationsList = Array.isArray(rawLocations) ? rawLocations : [rawLocations];
        locationsList
          .filter(Boolean)
          .map((code) => String(code).trim())
          .filter(Boolean)
          .forEach((code) => {
            if (!seen.has(code)) {
              seen.add(code);
              options.push({ label: code, value: code });
            }
          });
      }

      if (this.department && !seen.has(this.department)) {
        options.unshift({ label: this.department, value: this.department });
      }

      return [
        { label: 'None', value: '' },
        ...options,
      ];
    },
  },
  watch: {
    department: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!newValue) {
          if (this.manualPhone !== null && this.manualPhone !== undefined) {
            this.phone = this.manualPhone;
          }
          return;
        }
        if (!oldValue) {
          this.manualPhone = this.phone;
        }
        this.syncPhoneWithLocation();
      },
    },
    phone(newValue) {
      if (!this.department) {
        this.manualPhone = newValue;
      }
    },
  },
  async mounted() {
    await Promise.all([this.loadManager(), this.loadLocationsForPortal()]);
  },
  methods: {
    getLocationValue(facility) {
      if (!facility || typeof facility !== 'object') {
        return '';
      }
      return facility.title || facility.name || facility.code || facility.locationCode || facility._id || '';
    },
    isLocationMatch(facility, value) {
      if (!facility || typeof facility !== 'object' || !value) {
        return false;
      }
      const candidates = [
        facility.title,
        facility.name,
        facility.code,
        facility.locationCode,
        facility._id,
      ];
      return candidates.some((candidate) => candidate && String(candidate) === String(value));
    },
    getLocationLabel(facility, fallbackValue) {
      if (!facility || typeof facility !== 'object') {
        return fallbackValue || '';
      }
      return facility.title || facility.name || facility.code || facility._id || fallbackValue || '';
    },
    getLocationPhone(facility) {
      if (!facility || typeof facility !== 'object') {
        return '';
      }
      return this.normalizeValue(
        (facility.manager && facility.manager.phone)
        || facility.phone
        || (facility.contact && facility.contact.phone)
        || facility.contactPhone
        || '',
      );
    },
    getLocationAddress(facility) {
      if (!facility || typeof facility !== 'object') {
        return '';
      }
      const addressInfo = facility.addressInfo || {};
      const addressLine1 = addressInfo.addressLine1 || facility.addressLine1 || facility.address || '';
      const addressLine2 = addressInfo.addressLine2 || facility.addressLine2 || facility.address2 || '';
      const city = addressInfo.city || facility.city || '';
      const state = addressInfo.state || facility.state || '';
      const pinCode = addressInfo.pinCode || facility.pinCode || facility.postalCode || '';
      return [addressLine1, addressLine2, city, state, pinCode].filter(Boolean).join(', ');
    },
    normalizeValue(value) {
      if (Array.isArray(value)) {
        return value[0] || '';
      }
      return value || '';
    },
    setProfile(url) {
      this.profile = url;
    },
    clearProfile() {
      this.profile = null;
    },
    async loadLocationsForPortal() {
      try {
        const locations = await getLocationsForPortal(this.$keycloak.token);
        this.portalLocations = Array.isArray(locations) ? locations : [];
        this.resolveDepartmentTitle();
        this.syncPhoneWithLocation();
      } catch (err) {
        this.portalLocations = [];
      }
    },
    resolveDepartmentTitle() {
      if (!this.department || !Array.isArray(this.portalLocations) || !this.portalLocations.length) {
        return;
      }
      const match = this.portalLocations.find((facility) => this.isLocationMatch(facility, this.department));
      if (match && match.title && this.department !== match.title) {
        this.department = match.title;
      }
    },
    syncPhoneWithLocation() {
      if (!this.department) {
        return;
      }
      const match = (this.portalLocations || []).find((facility) => this.isLocationMatch(facility, this.department));
      if (!match) {
        return;
      }
      const locationPhone = this.getLocationPhone(match);
      if (locationPhone) {
        this.phone = locationPhone;
      }
    },
    async loadManager() {
      const managerId = this.managerId;
      if (!managerId) {
        return;
      }
      try {
        const response = await axios.get(`${AUTH_API_BASE}/manager/self/${managerId}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        if (response.data.error) {
          throw new Error(response.data.error);
        }
        this.manager = response.data;
      } catch (err) {
        this.manager = {
          id: managerId,
          attributes: {
            profile: this.$keycloak.tokenParsed.profile || '',
            phone: this.$keycloak.tokenParsed.phone || '',
            department: this.$keycloak.tokenParsed.department || '',
            title: this.$keycloak.tokenParsed.title || '',
            locations: this.$keycloak.tokenParsed.locations || [],
          },
        };
      }

      if (this.manager && this.manager.attributes) {
        this.profile = this.normalizeValue(this.manager.attributes.profile) || null;
        this.phone = this.normalizeValue(this.manager.attributes.phone) || null;
        const department = this.normalizeValue(this.manager.attributes.department);
        this.department = department === 'None' ? '' : department;
        this.title = this.normalizeValue(this.manager.attributes.title) || null;
        if (!this.department) {
          this.manualPhone = this.phone;
        }
        this.resolveDepartmentTitle();
      }
    },
    async saveProfileUpdates() {
      try {
        const managerId = this.managerId || (this.manager && this.manager.id);
        if (!managerId) {
          return;
        }
        await axios.post(`${AUTH_API_BASE}/manager/self/${managerId}`, {
          email: this.$keycloak.tokenParsed.email,
          username: this.$keycloak.tokenParsed.preferred_username,
          firstName: this.$keycloak.tokenParsed.given_name,
          lastName: this.$keycloak.tokenParsed.family_name,
          attributes: {
            locations: this.$keycloak.tokenParsed.locations || [],
            title: this.$keycloak.tokenParsed.title || '',
            profile: this.profile,
            phone: this.phone,
            department: this.department,
          },
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        if (this.manager && this.manager.attributes) {
          this.manager.attributes.profile = this.profile;
          this.manager.attributes.phone = this.phone;
          this.manager.attributes.department = this.department;
        }
        this.profileDialog = false;
        this.$message.success({
          title: 'Success',
          message: 'Profile updated successfully',
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.response && err.response.data ? err.response.data : err.message,
        });
      }
    },
  },
};
</script>

<style scoped>
.el-icon-success {
  color: rgb(43, 182, 43);
  margin-top: -8px;
}

</style>



// WEBPACK FOOTER //
// src/components/Profile.vue